import React, { useState } from "react";
import { Box, Paper, Typography, Popover, CircularProgress, IconButton, Tooltip, LinearProgress } from "@mui/material";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import HomeIcon from "@mui/icons-material/Home";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import HeightIcon from "@mui/icons-material/Height";
import SpeedIcon from "@mui/icons-material/Speed";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DownloadIcon from "@mui/icons-material/Download";
import PublicIcon from "@mui/icons-material/Public";
import PublicOffIcon from "@mui/icons-material/PublicOff";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import GroupsIcon from "@mui/icons-material/Groups";
import { FireKPIData } from "../types/fireKPI";
import { useUnitSystem } from "../hooks/useUnitSystem";
import { formatArea, formatFirelineIntensity, formatLength, formatNumber } from "../utils/units";
import { downloadFireSimulationFrame } from "../api/simulations";
import {
  isLayerBusy,
  usePublishFireToGis,
  useSimulationGisPublished,
  useUnpublishFireFromGis,
} from "../api/gis";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";

interface FireKPIPopoverProps {
  anchorPosition: { top: number; left: number } | null;
  onClose: () => void;
  kpis: FireKPIData | null;
  timestamp: dayjs.Dayjs | null;
  loading: boolean;
  simulationId?: string | null;
  partition?: string | null;
  frame?: number | null;
  projectName?: string;
  strategyName?: string;
  ignitionRegimeName?: string;
  simulationName?: string;
}

const FireKPIPopover: React.FC<FireKPIPopoverProps> = ({
  anchorPosition,
  onClose,
  kpis,
  timestamp,
  loading,
  simulationId,
  partition,
  frame,
  projectName,
  strategyName,
  ignitionRegimeName,
  simulationName,
}) => {
  const { projectId } = useParams<{ projectId: string }>();
  const unitSystem = useUnitSystem();
  const open = Boolean(anchorPosition);
  const MILES_PER_HOUR_PER_KM_PER_HOUR = 0.621371;
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!projectId || !simulationId || !partition || frame === null || frame === undefined) {
      return;
    }

    try {
      setDownloading(true);
      await downloadFireSimulationFrame(
        projectId,
        simulationId,
        partition,
        frame,
        projectName,
        strategyName,
        ignitionRegimeName,
        simulationName
      );
    } catch (error) {
      console.error('Failed to download fire simulation frame:', error);
    } finally {
      setDownloading(false);
    }
  };

  const canDownload = !loading && !kpis?.message && simulationId && partition && frame !== null && frame !== undefined;

  // "Add to GIS": publish this fire's four rasters (arrival, ROS, intensity,
  // flame height) to the tenant's GeoServer workspace so the customer's GIS
  // can read them as WMS/WMTS/WCS. Only offered when the environment has a
  // GeoServer (Admin → GIS Services shows the URLs and credentials).
  const { data: gisState } = useSimulationGisPublished(projectId, simulationId);
  const publishToGis = usePublishFireToGis(projectId, simulationId);
  const unpublishFromGis = useUnpublishFireFromGis(projectId, simulationId);
  const [gisError, setGisError] = useState<string | null>(null);
  const gisRow = partition
    ? gisState?.published.find((row) => row.partition === partition) ?? null
    : null;
  const gisEnabled = !!gisState?.enabled;
  const gisBusy = isLayerBusy(gisRow) || publishToGis.isPending || unpublishFromGis.isPending;
  const canPublish = !!(canDownload && gisEnabled);

  const handleGisClick = async () => {
    if (!partition) return;
    setGisError(null);
    try {
      if (gisRow && gisRow.status === "PUBLISHED") {
        await unpublishFromGis.mutateAsync(gisRow.id);
      } else if (gisRow && gisRow.status === "FAILED") {
        await publishToGis.mutateAsync(partition);
      } else if (!gisRow) {
        await publishToGis.mutateAsync(partition);
      }
    } catch (error) {
      setGisError((error as { message?: string })?.message || "GIS publish failed");
    }
  };

  const gisTooltip = gisError
    ? gisError
    : !gisRow
      ? "Add to GIS — publish this fire's rasters as WMS/WMTS/WCS layers"
      : gisRow.status === "PUBLISHED"
        ? `In your GIS as fire ${gisRow.layer_group} — click to remove`
        : gisRow.status === "FAILED"
          ? `GIS publish failed: ${gisRow.publish_error || "unknown error"} — click to retry`
          : gisRow.status === "UNPUBLISHING"
            ? "Removing from GIS…"
            : `Publishing to GIS… (${gisRow.publish_stage || "queued"})`;

  const formatIntensity = (value: number | null): string => {
    if (value === null) return "N/A";
    return formatFirelineIntensity(value, unitSystem);
  };

  const formatFlameHeight = (value: number | null): string => {
    if (value === null) return "N/A";
    return formatLength(value, unitSystem);
  };

  const formatROS = (value: number | null): string => {
    if (value === null) return "N/A";
    const kmPerHour = value;
    if (unitSystem === "imperial") {
      return `${formatNumber(kmPerHour * MILES_PER_HOUR_PER_KM_PER_HOUR, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} mph`;
    }
    return `${formatNumber(kmPerHour, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} km/h`;
  };

  const formatArrivalTime = (value: number | null): string => {
    if (value === null) return "N/A";
    const totalMinutes = Math.round(value * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatFireSize = (value: number | null): string => {
    if (value === null) return "N/A";
    return formatArea(value, unitSystem);
  };

  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition || undefined}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
    >
      <Paper sx={{ p: 2, maxWidth: 320, bgcolor: "#212b37", color: "white" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            {timestamp ? timestamp.format("DD/MM/YYYY HH:mm") : "Fire Data"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          {canPublish && (
            <Tooltip title={gisTooltip}>
              <span>
                <IconButton
                  onClick={handleGisClick}
                  disabled={gisBusy}
                  size="small"
                  sx={{
                    color: gisRow?.status === "FAILED" || gisError ? "#ff8a80" : gisRow?.status === "PUBLISHED" ? "#00d4ad" : "rgba(255, 255, 255, 0.7)",
                    "&:hover": { bgcolor: "rgba(0, 212, 173, 0.1)" },
                    "&:disabled": { color: "rgba(255, 255, 255, 0.3)" },
                  }}
                >
                  {gisBusy ? (
                    <CircularProgress size={20} sx={{ color: "#00d4ad" }} />
                  ) : gisRow?.status === "FAILED" || gisError ? (
                    <ErrorOutlineIcon fontSize="small" />
                  ) : gisRow?.status === "PUBLISHED" ? (
                    <PublicOffIcon fontSize="small" />
                  ) : (
                    <PublicIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canDownload && (
            <Tooltip title="Download GeoTIFF (ZIP)">
              <IconButton
                onClick={handleDownload}
                disabled={downloading}
                size="small"
                sx={{
                  color: "#00d4ad",
                  "&:hover": { bgcolor: "rgba(0, 212, 173, 0.1)" },
                  "&:disabled": { color: "rgba(255, 255, 255, 0.3)" },
                }}
              >
                {downloading ? (
                  <CircularProgress size={20} sx={{ color: "#00d4ad" }} />
                ) : (
                  <DownloadIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
          </Box>
        </Box>
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} sx={{ color: "#00d4ad" }} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading fire data...
            </Typography>
          </Box>
        ) : kpis?.message ? (
          <Box>
            <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
              {kpis.message}
            </Typography>
          </Box>
        ) : (
          <Box>

            {kpis && kpis.member_burn_probability_final !== null && kpis.member_burn_probability_final !== undefined && (
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                  <GroupsIcon sx={{ mr: 1, fontSize: 18, color: "#a78bfa" }} />
                  <Typography variant="body2">
                    Members reaching here:{" "}
                    <b>{Math.round((kpis.member_burn_probability_final || 0) * 100)}%</b>
                    {kpis.ensemble_members
                      ? ` (${Math.round((kpis.member_burn_probability_final || 0) * kpis.ensemble_members)} of ${kpis.ensemble_members})`
                      : ""}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, (kpis.member_burn_probability_final || 0) * 100)}
                  sx={{
                    height: 6, borderRadius: 3, bgcolor: "rgba(255,255,255,0.12)",
                    "& .MuiLinearProgress-bar": { bgcolor: "#a78bfa", borderRadius: 3 },
                  }}
                />
                {kpis.member_burn_probability !== null && kpis.member_burn_probability !== undefined && (
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                    {Math.round((kpis.member_burn_probability || 0) * 100)}% reached by this frame
                  </Typography>
                )}
              </Box>
            )}

            {kpis?.fire_size_ha !== null && kpis && (
              <Typography
                variant="body2"
                sx={{ mb: 1, display: "flex", alignItems: "center" }}
              >
                <WhatshotIcon sx={{ mr: 1, fontSize: 18 }} />
                Fire Size: {formatFireSize(kpis.fire_size_ha)}
              </Typography>
            )}

            {kpis?.assets_impacted !== null && kpis && (
              <Typography
                variant="body2"
                sx={{ mb: 1, display: "flex", alignItems: "center" }}
              >
                <HomeIcon sx={{ mr: 1, fontSize: 18 }} />
                Assets Impacted: {kpis.assets_impacted}
              </Typography>
            )}

            {kpis?.fireline_intensity_kw_m !== null && kpis && (
              <Typography
                variant="body2"
                sx={{ mb: 1, display: "flex", alignItems: "center" }}
              >
                <LocalFireDepartmentIcon sx={{ mr: 1, fontSize: 18 }} />
                Fireline Intensity: {formatIntensity(kpis.fireline_intensity_kw_m)}
              </Typography>
            )}

            {kpis?.flame_height_m !== null && kpis && (
              <Typography
                variant="body2"
                sx={{ mb: 1, display: "flex", alignItems: "center" }}
              >
                <HeightIcon sx={{ mr: 1, fontSize: 18 }} />
                Flame Height: {formatFlameHeight(kpis.flame_height_m)}
              </Typography>
            )}

            {kpis?.rate_of_spread_km_h !== null && kpis && (
              <Typography
                variant="body2"
                sx={{ mb: 1, display: "flex", alignItems: "center" }}
              >
                <SpeedIcon sx={{ mr: 1, fontSize: 18 }} />
                Rate of Spread: {formatROS(kpis.rate_of_spread_km_h)}
              </Typography>
            )}

            {kpis?.arrival_time_hours !== null && kpis && (
              <Typography
                variant="body2"
                sx={{ mb: 0, display: "flex", alignItems: "center" }}
              >
                <AccessTimeIcon sx={{ mr: 1, fontSize: 18 }} />
                {kpis.arrival_p10_hours != null || kpis.arrival_p90_hours != null
                  ? "Median Arrival: "
                  : "Arrival Time: "}
                {formatArrivalTime(kpis.arrival_time_hours)}
              </Typography>
            )}

            {kpis && (kpis.arrival_p10_hours != null || kpis.arrival_p90_hours != null) && (
              <Typography variant="caption" sx={{ ml: "26px", color: "rgba(255,255,255,0.6)", display: "block" }}>
                90% CI {formatArrivalTime(kpis.arrival_p10_hours ?? null)}–{formatArrivalTime(kpis.arrival_p90_hours ?? null)}
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    </Popover>
  );
};

export default FireKPIPopover;
